/* Wheel Fitment Calculator - Shopify Section
 * Vanilla-JS port of the React "wheel-fit-pro" source (24-June update).
 * Mirrors: calcFitment.jsx, SummaryText.jsx, ResultsTable.jsx,
 *          FitmentDiagram.jsx, SpeedoTable.jsx, FitmentCalculator.jsx
 */
(function () {
  'use strict';

  /* ────────────────────────────── Constants ────────────────────────────── */
  var IN_TO_MM = 25.4;

  // Tyre "measuring rim" reference table (section width mm -> measuring rim in)
  var MEASURING_RIM = {
    155: 4.5, 165: 4.5, 175: 5.0, 185: 5.5, 195: 6.0,
    205: 6.5, 215: 7.0, 225: 7.5, 235: 7.5, 245: 8.0,
    255: 8.0, 265: 8.5, 275: 9.0, 285: 9.0, 295: 9.5,
    305: 10.0, 315: 10.5, 325: 11.0, 335: 11.0
  };

  // Diagram geometry / theming (from FitmentDiagram.jsx)
  var DIA_W = 380;
  var DIA_H = 520;
  var DIA_HX = 148;   // hub centre X
  var DIA_CY = 260;   // hub centre Y
  var OLD_COLOR = '#c2410c';
  var NEW_COLOR = '#1e3a8a';
  var SUSPENSION_IMG = 'https://media.base44.com/images/public/695b6240da80065dc84f7b0c/88e80adde_Untitleddesign66.png';

  var HTML2CANVAS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';

  var FIELDS = ['rim_in', 'wheel_w_in', 'et_mm', 'tire_w_mm', 'profile_pct'];

  /* ───────────────────────── Icons (lucide-style) ───────────────────────── */
  function icon(paths, extraAttrs) {
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" ' +
      'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"' +
      (extraAttrs ? ' ' + extraAttrs : '') + '>' + paths + '</svg>';
  }
  var IC_ALERT = '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>';
  var IC_CHECK = '<circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>';
  var IC_INFO  = '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>';
  var IC_GAUGE = '<path d="m12 14 4-4"/><path d="M3.34 19a10 10 0 1 1 17.32 0"/>';

  /* ───────────────────────── Calculation (calcFitment.jsx) ───────────────── */
  function effectiveWidth(tire_w_mm, wheel_w_in) {
    var sortedKeys = Object.keys(MEASURING_RIM).map(Number).sort(function (a, b) { return a - b; });
    var key = sortedKeys[0];
    for (var i = 0; i < sortedKeys.length; i++) {
      var k = sortedKeys[i];
      if (k <= tire_w_mm) key = k; else break;
    }
    var measRim = MEASURING_RIM[key];
    var steps = (measRim - wheel_w_in) / 0.5;
    return steps > 0 ? tire_w_mm - (steps * 5) : tire_w_mm;
  }

  function round1(n) { return Math.round(n * 10) / 10; }

  function validateSetup(setup) {
    var errors = [];
    if (!setup.rim_in || setup.rim_in < 10 || setup.rim_in > 26) {
      errors.push('Rim diameter must be between 10" and 26"');
    }
    if (!setup.wheel_w_in || setup.wheel_w_in < 4 || setup.wheel_w_in > 16) {
      errors.push('Wheel width must be between 4" and 16"');
    }
    if (setup.et_mm === undefined || setup.et_mm < -50 || setup.et_mm > 80) {
      errors.push('ET (offset) must be between -50mm and 80mm');
    }
    if (!setup.tire_w_mm || setup.tire_w_mm < 125 || setup.tire_w_mm > 400) {
      errors.push('Tire width must be between 125mm and 400mm');
    }
    if (!setup.profile_pct || setup.profile_pct < 15 || setup.profile_pct > 85) {
      errors.push('Profile must be between 15% and 85%');
    }
    return errors;
  }

  function metrics(s) {
    var rim_mm = Math.round(s.rim_in * IN_TO_MM);
    var eff_w = effectiveWidth(s.tire_w_mm, s.wheel_w_in);
    var sidewall_mm = Math.floor(eff_w * (s.profile_pct / 100));
    var diam_mm = rim_mm + (2 * sidewall_mm);
    var circ_mm = Math.round(Math.PI * diam_mm);

    var wheel_w_mm_raw = s.wheel_w_in * IN_TO_MM;
    var wheel_half_raw = wheel_w_mm_raw / 2;

    var poke_raw = wheel_half_raw - s.et_mm;
    var inset_raw = wheel_half_raw + s.et_mm;

    var tire_half_raw = s.tire_w_mm / 2;
    var tire_overhang_raw = tire_half_raw - wheel_half_raw;

    var tire_poke_raw = poke_raw + tire_overhang_raw;
    var tire_inset_raw = inset_raw + tire_overhang_raw;

    return {
      poke_raw: poke_raw,
      inset_raw: inset_raw,
      tire_poke_raw: tire_poke_raw,
      tire_inset_raw: tire_inset_raw,
      diam_mm: diam_mm,
      circ_mm: circ_mm,
      sidewall_mm: Math.round(sidewall_mm),
      rim_mm: rim_mm,
      poke_mm: Math.round(poke_raw),
      inset_mm: Math.round(inset_raw),
      tire_poke_mm: Math.round(tire_poke_raw),
      tire_inset_mm: Math.round(tire_inset_raw),
      wheel_w_mm: Math.round(wheel_w_mm_raw)
    };
  }

  function calcFitment(oldSetup, newSetup) {
    var oldErrors = validateSetup(oldSetup);
    var newErrors = validateSetup(newSetup);

    if (oldErrors.length > 0 || newErrors.length > 0) {
      return { valid: false, errors: { old: oldErrors, new: newErrors } };
    }

    var oldM = metrics(oldSetup);
    var newM = metrics(newSetup);

    var diam_delta_mm = newM.diam_mm - oldM.diam_mm;
    var circ_delta_mm = newM.circ_mm - oldM.circ_mm;
    var outer_change = Math.round(newM.poke_raw - oldM.poke_raw);
    var inner_change = Math.round(newM.inset_raw - oldM.inset_raw);
    var tire_outer_change = Math.round(newM.tire_poke_raw - oldM.tire_poke_raw);
    var tire_inner_change = Math.round(newM.tire_inset_raw - oldM.tire_inset_raw);

    var rideHeightChange = Math.round((newM.diam_mm - oldM.diam_mm) / 2);

    var ratio = oldM.circ_mm / newM.circ_mm;
    var speedoErr = (ratio - 1) * 100;
    var actual30 = 30 * ratio;
    var actual60 = 60 * ratio;

    return {
      valid: true,
      old: oldM,
      new: newM,
      delta: {
        inner_change_mm: inner_change,
        outer_change_mm: outer_change,
        tire_inner_change_mm: tire_inner_change,
        tire_outer_change_mm: tire_outer_change,
        speedo_error_pct: round1(speedoErr),
        actual_30_mph: round1(actual30),
        actual_60_mph: round1(actual60),
        ride_height_change_mm: rideHeightChange,
        diameter_change_mm: diam_delta_mm,
        circumference_change_mm: circ_delta_mm
      }
    };
  }

  /* ────────────────────────────── Renderers ─────────────────────────────── */

  // SummaryText.jsx
  function renderSummary(container, results) {
    var delta = results.delta;
    var innerChange = delta.inner_change_mm;
    var outerChange = delta.outer_change_mm;
    var heightChange = delta.ride_height_change_mm;

    var hasIssues = Math.abs(innerChange) > 15 || Math.abs(outerChange) > 15 || Math.abs(delta.speedo_error_pct) > 5;
    var isSafe = Math.abs(innerChange) <= 10 && Math.abs(outerChange) <= 10;

    var stats = [];
    if (innerChange !== 0) {
      stats.push({
        value: Math.abs(innerChange),
        label: 'Inner clearance',
        direction: innerChange > 0 ? 'closer to strut' : 'away from strut',
        warning: Math.abs(innerChange) > 15
      });
    }
    if (outerChange !== 0) {
      stats.push({
        value: Math.abs(outerChange),
        label: 'Outer poke',
        direction: outerChange > 0 ? 'further out' : 'tucked in',
        warning: Math.abs(outerChange) > 15
      });
    }
    if (heightChange !== 0) {
      stats.push({
        value: Math.abs(heightChange),
        label: 'Ride height',
        direction: heightChange > 0 ? 'increase (less arch gap)' : 'decrease (more arch gap)',
        warning: false
      });
    }

    var stateClass = hasIssues ? 'wfc-warning' : (isSafe ? 'wfc-safe' : 'wfc-info');
    var titleText = hasIssues ? 'Fitment Changes Detected' : (isSafe ? 'Good Fitment Match' : 'Fitment Summary');
    var titleIcon = hasIssues ? IC_ALERT : (isSafe ? IC_CHECK : IC_INFO);

    var html = '';
    html += '<div class="wfc-summary ' + stateClass + '">';
    html += '  <div class="wfc-summary-header">';
    html += '    <span class="wfc-summary-icon">' + icon(titleIcon) + '</span>';
    html += '    <h3 class="wfc-summary-title">' + titleText + '</h3>';
    html += '  </div>';

    if (stats.length > 0) {
      html += '<div class="wfc-summary-pills">';
      for (var i = 0; i < stats.length; i++) {
        var s = stats[i];
        html += '<div class="wfc-summary-pill">';
        html += '<span class="wfc-summary-pill-value"' +
          (hasIssues ? ' data-countup="' + s.value + '"' : '') + '>' +
          (hasIssues ? '0' : s.value) + 'mm</span>';
        html += '<span class="wfc-summary-pill-label">' + s.label + ' &mdash; ' + s.direction + '</span>';
        if (s.warning) {
          html += '<span class="wfc-summary-pill-warn">' + icon(IC_ALERT) + '</span>';
        }
        html += '</div>';
      }
      html += '</div>';
    } else {
      html += '<p class="wfc-summary-empty">The new setup has identical fitment to the existing setup.</p>';
    }

    if (hasIssues) {
      html += '<div class="wfc-summary-note">' + icon(IC_ALERT) +
        '<p>Significant changes detected. Verify clearances with your vehicle before purchasing.</p></div>';
    }
    html += '</div>';

    container.innerHTML = html;

    if (hasIssues) {
      var els = container.querySelectorAll('.wfc-summary-pill-value[data-countup]');
      for (var j = 0; j < els.length; j++) {
        countUp(els[j], parseInt(els[j].getAttribute('data-countup'), 10), 800);
      }
    }
  }

  function countUp(el, target, duration) {
    if (!window.requestAnimationFrame || typeof performance === 'undefined') {
      el.textContent = target + 'mm';
      return;
    }
    var start = performance.now();
    function tick(now) {
      var progress = Math.min((now - start) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = Math.round(eased * target) + 'mm';
      if (progress < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  // ResultsTable.jsx
  function renderTable(container, results) {
    var o = results.old, n = results.new, d = results.delta;
    var speedoSign = d.speedo_error_pct > 0 ? '+' : '';
    var rhSign = d.ride_height_change_mm > 0 ? '+' : '';

    var rows = [
      { label: 'Diameter', old: o.diam_mm + 'mm', neu: n.diam_mm + 'mm' },
      { label: 'Circumference', old: o.circ_mm + 'mm', neu: n.circ_mm + 'mm' },
      { label: 'Poke', old: o.poke_mm + 'mm', neu: n.poke_mm + 'mm' },
      { label: 'Inset', old: o.inset_mm + 'mm', neu: n.inset_mm + 'mm' },
      { label: 'Speedo error', old: speedoSign + '0%', neu: speedoSign + d.speedo_error_pct + '%' },
      { label: 'Reading at 30mph', old: '30mph', neu: d.actual_30_mph + 'mph' },
      { label: 'Reading at 60mph', old: '60mph', neu: d.actual_60_mph + 'mph' },
      { label: 'Ride height gain', old: '0mm', neu: rhSign + d.ride_height_change_mm + 'mm' },
      { label: 'Arch gap loss', old: '0mm', neu: rhSign + d.ride_height_change_mm + 'mm' }
    ];

    var html = '<div class="wfc-table-card"><div class="wfc-table-scroll"><table class="wfc-table">';
    html += '<thead><tr><th></th><th>Existing</th><th>New</th></tr></thead><tbody>';
    for (var i = 0; i < rows.length; i++) {
      html += '<tr><td>' + rows[i].label + '</td><td>' + rows[i].old + '</td><td>' + rows[i].neu + '</td></tr>';
    }
    html += '</tbody></table></div></div>';
    container.innerHTML = html;
  }

  // SpeedoTable.jsx
  function renderSpeedo(container, results) {
    var d = results.delta;
    var pct = d.speedo_error_pct;
    var isAccurate = Math.abs(pct) < 3;
    var pctSign = pct > 0 ? '+' : '';

    var badge = isAccurate
      ? '<span class="wfc-speedo-badge wfc-minimal">' + icon(IC_CHECK) + 'Minimal Impact</span>'
      : '<span class="wfc-speedo-badge wfc-noticeable">' + icon(IC_ALERT) + 'Noticeable Change</span>';

    var errClass = isAccurate ? 'wfc-good' : 'wfc-warn';

    var desc;
    if (pct > 0) {
      desc = 'When your speedometer shows <strong>60 mph</strong>, you\'ll actually be traveling at ' +
        '<span class="wfc-highlight">' + d.actual_60_mph + ' mph</span> (slower than displayed).';
    } else if (pct < 0) {
      desc = 'When your speedometer shows <strong>60 mph</strong>, you\'ll actually be traveling at ' +
        '<span class="wfc-highlight">' + d.actual_60_mph + ' mph</span> (faster than displayed).';
    } else {
      desc = 'Your speedometer accuracy will remain unchanged.';
    }

    var html = '';
    html += '<div class="wfc-speedo-card">';
    html += '  <div class="wfc-speedo-header">';
    html += '    <div class="wfc-speedo-title-group">';
    html += '      <div class="wfc-speedo-icon">' + icon(IC_GAUGE) + '</div>';
    html += '      <div><h3 class="wfc-speedo-title">Speedometer Impact</h3>';
    html += '      <p class="wfc-speedo-subtitle">How your speedo reading will change</p></div>';
    html += '    </div>' + badge;
    html += '  </div>';
    html += '  <div class="wfc-speedo-content">';
    html += '    <div class="wfc-speedo-grid">';
    html += '      <div class="wfc-speedo-stat"><p class="wfc-speedo-stat-label">Speedo Error</p>' +
            '<p class="wfc-speedo-stat-value ' + errClass + '">' + pctSign + pct + '%</p></div>';
    html += '      <div class="wfc-speedo-stat"><p class="wfc-speedo-stat-label">Actual Speed at 30 mph Indicated</p>' +
            '<p class="wfc-speedo-stat-value wfc-neutral">' + d.actual_30_mph + ' <span class="wfc-speedo-stat-unit">mph</span></p></div>';
    html += '      <div class="wfc-speedo-stat"><p class="wfc-speedo-stat-label">Actual Speed at 60 mph Indicated</p>' +
            '<p class="wfc-speedo-stat-value wfc-neutral">' + d.actual_60_mph + ' <span class="wfc-speedo-stat-unit">mph</span></p></div>';
    html += '    </div>';
    html += '    <div class="wfc-speedo-description">' + desc + '</div>';
    html += '  </div>';
    html += '</div>';
    container.innerHTML = html;
  }

  // FitmentDiagram.jsx — single tyre/rim cross-section outline
  function outlinePath(m, setup, scale) {
    var cx = DIA_HX + setup.et_mm * scale;
    var cy = DIA_CY;

    var tyre_left = cx - (setup.tire_w_mm / 2) * scale;
    var tyre_right = cx + (setup.tire_w_mm / 2) * scale;
    var tyre_top = cy - (m.diam_mm / 2) * scale;
    var tyre_bottom = cy + (m.diam_mm / 2) * scale;

    var rim_left = cx - (m.wheel_w_mm / 2) * scale;
    var rim_right = cx + (m.wheel_w_mm / 2) * scale;
    var rim_top = cy - (m.rim_mm / 2) * scale;
    var rim_bottom = cy + (m.rim_mm / 2) * scale;

    var r = Math.min(m.sidewall_mm * scale * 0.20, 18);

    var tread_half = (setup.tire_w_mm * 0.92 / 2) * scale;
    var tread_left = cx - tread_half;
    var tread_right = cx + tread_half;

    var bulge = m.sidewall_mm * scale * 0.08;
    var ch = 5, fb = 4, fi = 8;

    var sw_ctrl_up = (rim_top - tyre_top - r) * 0.45;
    var sw_ctrl_dn = ((tyre_bottom - r) - rim_bottom) * 0.45;

    var d = [
      'M ' + tyre_left + ' ' + (tyre_top + r),
      'A ' + r + ' ' + r + ' 0 0 1 ' + (tyre_left + r) + ' ' + tyre_top,
      'L ' + tread_left + ' ' + tyre_top,
      'L ' + tread_right + ' ' + tyre_top,
      'A ' + r + ' ' + r + ' 0 0 1 ' + tyre_right + ' ' + (tyre_top + r),
      'C ' + (tyre_right + bulge) + ' ' + (tyre_top + r + sw_ctrl_up * 0.8) + ',',
      '  ' + (tyre_right + bulge) + ' ' + (rim_top - sw_ctrl_up * 0.2) + ',',
      '  ' + tyre_right + ' ' + rim_top,
      'L ' + (tyre_right - ch) + ' ' + rim_top,
      'L ' + tyre_right + ' ' + (rim_top + ch),
      'L ' + rim_right + ' ' + (rim_top + fi),
      'A ' + fb + ' ' + fb + ' 0 0 1 ' + rim_right + ' ' + (rim_top + fi + fb * 2),
      'L ' + rim_right + ' ' + (rim_bottom - fi - fb * 2),
      'A ' + fb + ' ' + fb + ' 0 0 1 ' + rim_right + ' ' + (rim_bottom - fi),
      'L ' + tyre_right + ' ' + (rim_bottom - ch),
      'L ' + (tyre_right - ch) + ' ' + rim_bottom,
      'L ' + tyre_right + ' ' + rim_bottom,
      'C ' + (tyre_right + bulge) + ' ' + (rim_bottom + sw_ctrl_dn * 0.2) + ',',
      '  ' + (tyre_right + bulge) + ' ' + (tyre_bottom - r - sw_ctrl_dn * 0.8) + ',',
      '  ' + tyre_right + ' ' + (tyre_bottom - r),
      'A ' + r + ' ' + r + ' 0 0 1 ' + (tyre_right - r) + ' ' + tyre_bottom,
      'L ' + tread_right + ' ' + tyre_bottom,
      'L ' + tread_left + ' ' + tyre_bottom,
      'L ' + (tyre_left + r) + ' ' + tyre_bottom,
      'A ' + r + ' ' + r + ' 0 0 1 ' + tyre_left + ' ' + (tyre_bottom - r),
      'C ' + (tyre_left - bulge) + ' ' + (tyre_bottom - r - sw_ctrl_dn * 0.2) + ',',
      '  ' + (tyre_left - bulge) + ' ' + (rim_bottom + sw_ctrl_dn * 0.8) + ',',
      '  ' + tyre_left + ' ' + rim_bottom,
      'L ' + (tyre_left + ch) + ' ' + rim_bottom,
      'L ' + tyre_left + ' ' + (rim_bottom - ch),
      'L ' + rim_left + ' ' + (rim_bottom - fi),
      'A ' + fb + ' ' + fb + ' 0 0 0 ' + rim_left + ' ' + (rim_bottom - fi - fb * 2),
      'L ' + rim_left + ' ' + (rim_top + fi + fb * 2),
      'A ' + fb + ' ' + fb + ' 0 0 0 ' + rim_left + ' ' + (rim_top + fi),
      'L ' + tyre_left + ' ' + (rim_top + ch),
      'L ' + (tyre_left + ch) + ' ' + rim_top,
      'L ' + tyre_left + ' ' + rim_top,
      'C ' + (tyre_left - bulge) + ' ' + (rim_top - sw_ctrl_up * 0.2) + ',',
      '  ' + (tyre_left - bulge) + ' ' + (tyre_top + r + sw_ctrl_up * 0.8) + ',',
      '  ' + tyre_left + ' ' + (tyre_top + r),
      'Z'
    ].join(' ');

    var highlightH = Math.max(4, (tread_right - tread_left) * 0.07);

    return {
      d: d,
      highlight: { x: tread_left, y: tyre_top + 2, w: tread_right - tread_left, h: highlightH }
    };
  }

  function outlineSvg(m, setup, scale, color, isFront) {
    var o = outlinePath(m, setup, scale);
    var g = '<g' + (isFront ? ' filter="url(#wfc-shadow)"' : '') + '>';
    g += '<path d="' + o.d + '" fill="' + color + '" fill-opacity="0.10" stroke="none"/>';
    g += '<path d="' + o.d + '" fill="none" stroke="' + color + '" stroke-width="' + (isFront ? 3 : 2) + '"/>';
    g += '<rect x="' + o.highlight.x + '" y="' + o.highlight.y + '" width="' + o.highlight.w +
         '" height="' + o.highlight.h + '" rx="2" fill="white" opacity="0.18"/>';
    g += '</g>';
    return g;
  }

  function fmtDiagramLabel(prefix, s) {
    return prefix + ': ' + s.wheel_w_in + '"×' + s.rim_in + '" ET' + s.et_mm +
      ' — ' + s.tire_w_mm + '/' + s.profile_pct + ' R' + s.rim_in;
  }

  function renderDiagram(container, results, existingSetup, newSetup) {
    var oldM = results.old, newM = results.new;
    var maxDiam = Math.max(oldM.diam_mm, newM.diam_mm);
    var scale = (DIA_H * 0.90) / maxDiam;

    var svg = '';
    svg += '<svg class="wfc-diagram-svg" viewBox="0 0 ' + DIA_W + ' ' + DIA_H + '" ' +
           'preserveAspectRatio="xMidYMid meet" shape-rendering="geometricPrecision" ' +
           'text-rendering="optimizeLegibility" xmlns="http://www.w3.org/2000/svg">';
    svg += '<defs>';
    svg += '<filter id="wfc-shadow" x="-10%" y="-10%" width="120%" height="120%">';
    svg += '<feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000" flood-opacity="0.18"/></filter>';
    svg += '<clipPath id="wfc-diagram-clip"><rect width="' + DIA_W + '" height="' + DIA_H + '"/></clipPath>';
    svg += '</defs>';
    svg += '<rect width="' + DIA_W + '" height="' + DIA_H + '" fill="#ffffff"/>';
    svg += '<g clip-path="url(#wfc-diagram-clip)">';
    svg += '<image href="' + SUSPENSION_IMG + '" xlink:href="' + SUSPENSION_IMG + '" x="0" y="0" width="' +
           DIA_W + '" height="' + DIA_H + '" preserveAspectRatio="xMidYMid meet"/>';
    svg += outlineSvg(oldM, existingSetup, scale, OLD_COLOR, false);
    svg += outlineSvg(newM, newSetup, scale, NEW_COLOR, true);
    // Ground line
    svg += '<line x1="0" y1="' + (DIA_H - 14) + '" x2="' + DIA_W + '" y2="' + (DIA_H - 14) +
           '" stroke="#BDBDBD" stroke-width="1.5" stroke-dasharray="6 4"/>';
    svg += '<text x="' + (DIA_W - 8) + '" y="' + (DIA_H - 6) + '" text-anchor="end" font-size="9" ' +
           'fill="#AAAAAA" font-family="sans-serif">GROUND</text>';
    svg += '</g>';
    svg += '<rect width="' + DIA_W + '" height="' + DIA_H + '" fill="none" stroke="#CCCCCC" stroke-width="1"/>';
    svg += '</svg>';

    var html = '';
    html += '<div class="wfc-diagram-label wfc-diagram-label--new">' + fmtDiagramLabel('NEW', newSetup) + '</div>';
    html += '<div class="wfc-diagram-svg-wrap">' + svg + '</div>';
    html += '<div class="wfc-diagram-label wfc-diagram-label--existing">' + fmtDiagramLabel('EXISTING', existingSetup) + '</div>';
    container.innerHTML = html;
  }

  /* ────────────────────────────── Save (html2canvas) ─────────────────────── */
  var html2canvasPromise = null;
  function loadHtml2Canvas() {
    if (window.html2canvas) return Promise.resolve(window.html2canvas);
    if (html2canvasPromise) return html2canvasPromise;
    html2canvasPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = HTML2CANVAS_SRC;
      script.onload = function () { resolve(window.html2canvas); };
      script.onerror = function () { reject(new Error('Failed to load html2canvas')); };
      document.head.appendChild(script);
    });
    return html2canvasPromise;
  }

  function handleSave(root, saveBtn) {
    var target = root.querySelector('.wfc-results-content');
    if (!target) return;
    var original = saveBtn.innerHTML;
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving...';

    loadHtml2Canvas().then(function (html2canvas) {
      return html2canvas(target, { backgroundColor: '#0f172a', scale: 2, logging: false, useCORS: true });
    }).then(function (canvas) {
      var link = document.createElement('a');
      link.download = 'fitment-results.png';
      link.href = canvas.toDataURL();
      link.click();
    }).catch(function (error) {
      console.error('Failed to save screenshot:', error);
    }).then(function () {
      saveBtn.disabled = false;
      saveBtn.innerHTML = original;
    });
  }

  /* ────────────────────────────── Input handling ─────────────────────────── */
  function readSetup(root, which) {
    var setup = {};
    for (var i = 0; i < FIELDS.length; i++) {
      var input = root.querySelector('.wfc-form-input[data-setup="' + which + '"][data-field="' + FIELDS[i] + '"]');
      if (input) {
        var v = input.value;
        setup[FIELDS[i]] = v === '' ? '' : parseFloat(v);
      }
    }
    return setup;
  }

  function isComplete(setup) {
    for (var i = 0; i < FIELDS.length; i++) {
      var v = setup[FIELDS[i]];
      if (v === '' || v === undefined || (typeof v === 'number' && isNaN(v))) return false;
    }
    return true;
  }

  function updateTirePreview(root, which) {
    var setup = readSetup(root, which);
    var preview = root.querySelector('.wfc-tire-preview[data-setup="' + which + '"]');
    if (!preview) return;
    var sizeEl = preview.querySelector('.wfc-tire-size');
    if (setup.tire_w_mm && setup.profile_pct && setup.rim_in) {
      if (sizeEl) sizeEl.textContent = setup.tire_w_mm + '/' + setup.profile_pct + 'R' + setup.rim_in;
      preview.style.display = 'block';
    } else {
      preview.style.display = 'none';
    }
  }

  // Inject custom up/down stepper buttons on each number input (matches CSS)
  function enhanceSpinners(root) {
    var chevUp = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m18 15-6-6-6 6"/></svg>';
    var chevDown = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>';
    var inputs = root.querySelectorAll('.wfc-form-input');
    for (var i = 0; i < inputs.length; i++) {
      (function (input) {
        if (input.closest('.wfc-input-wrapper')) return; // already enhanced
        var wrapper = document.createElement('div');
        wrapper.className = 'wfc-input-wrapper';
        input.parentNode.insertBefore(wrapper, input);
        wrapper.appendChild(input);

        var spinners = document.createElement('div');
        spinners.className = 'wfc-spinner-buttons';
        var up = document.createElement('button');
        up.type = 'button';
        up.className = 'wfc-spinner-btn';
        up.setAttribute('aria-label', 'Increase');
        up.innerHTML = chevUp;
        var down = document.createElement('button');
        down.type = 'button';
        down.className = 'wfc-spinner-btn';
        down.setAttribute('aria-label', 'Decrease');
        down.innerHTML = chevDown;
        spinners.appendChild(up);
        spinners.appendChild(down);
        wrapper.appendChild(spinners);

        up.addEventListener('click', function () { stepInput(input, 1); });
        down.addEventListener('click', function () { stepInput(input, -1); });
      })(inputs[i]);
    }
  }

  function stepInput(input, dir) {
    var step = parseFloat(input.getAttribute('step')) || 1;
    var min = input.getAttribute('min');
    var max = input.getAttribute('max');
    var current = input.value === '' ? 0 : parseFloat(input.value);
    if (isNaN(current)) current = 0;
    var next = current + dir * step;
    if (max !== null && max !== '' && next > parseFloat(max)) next = parseFloat(max);
    if (min !== null && min !== '' && next < parseFloat(min)) next = parseFloat(min);
    // Avoid floating point noise (e.g. 0.1 increments)
    next = Math.round(next * 100) / 100;
    input.value = next;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /* ────────────────────────────── Errors ─────────────────────────────────── */
  function clearErrors(root) {
    var errs = root.querySelectorAll('.wfc-errors');
    for (var i = 0; i < errs.length; i++) errs[i].parentNode.removeChild(errs[i]);
    var banner = root.querySelector('.wfc-validation-error');
    if (banner) banner.style.display = 'none';
  }

  function showCardErrors(root, which, messages) {
    if (!messages || messages.length === 0) return;
    var card = root.querySelector('.wfc-setup-card[data-setup="' + which + '"] .wfc-card-content');
    if (!card) return;
    var box = document.createElement('div');
    box.className = 'wfc-errors';
    var html = '';
    for (var i = 0; i < messages.length; i++) {
      html += '<p class="wfc-error-text">' + messages[i] + '</p>';
    }
    box.innerHTML = html;
    card.appendChild(box);
  }

  /* ────────────────────────────── Section init ───────────────────────────── */
  function initSection(root) {
    if (!root || root.dataset.wfcInitialized === 'true') return;
    root.dataset.wfcInitialized = 'true';

    enhanceSpinners(root);

    var calcBtn = root.querySelector('.wfc-btn-calculate');
    var saveBtn = root.querySelector('.wfc-btn-save');
    var resultsEl = root.querySelector('.wfc-results');
    var emptyEl = root.querySelector('.wfc-empty-state');

    var summaryC = root.querySelector('.wfc-summary-container');
    var tableC = root.querySelector('.wfc-table-container');
    var diagramC = root.querySelector('.wfc-diagram-container');
    var speedoC = root.querySelector('.wfc-speedo-container');

    // Live tyre-size previews
    var allInputs = root.querySelectorAll('.wfc-form-input');
    for (var i = 0; i < allInputs.length; i++) {
      allInputs[i].addEventListener('input', function (e) {
        var which = e.target.getAttribute('data-setup');
        updateTirePreview(root, which);
      });
    }
    updateTirePreview(root, 'existing');
    updateTirePreview(root, 'new');

    function calculate() {
      var existingSetup = readSetup(root, 'existing');
      var newSetup = readSetup(root, 'new');

      clearErrors(root);

      // All values present?
      if (!isComplete(existingSetup) || !isComplete(newSetup)) {
        if (resultsEl) resultsEl.classList.remove('wfc-visible');
        if (emptyEl) emptyEl.classList.remove('wfc-hidden');
        if (saveBtn) saveBtn.style.display = 'none';
        return;
      }

      var results = calcFitment(existingSetup, newSetup);

      if (!results.valid) {
        if (resultsEl) resultsEl.classList.remove('wfc-visible');
        if (emptyEl) emptyEl.classList.add('wfc-hidden');
        if (saveBtn) saveBtn.style.display = 'none';
        showCardErrors(root, 'existing', results.errors.old);
        showCardErrors(root, 'new', results.errors.new);
        showValidationBanner(root);
        return;
      }

      if (summaryC) renderSummary(summaryC, results);
      if (tableC) renderTable(tableC, results);
      if (diagramC) renderDiagram(diagramC, results, existingSetup, newSetup);
      if (speedoC) renderSpeedo(speedoC, results);

      if (resultsEl) resultsEl.classList.add('wfc-visible');
      if (emptyEl) emptyEl.classList.add('wfc-hidden');
      if (saveBtn) saveBtn.style.display = '';
    }

    if (calcBtn) calcBtn.addEventListener('click', calculate);
    if (saveBtn) saveBtn.addEventListener('click', function () { handleSave(root, saveBtn); });
  }

  function showValidationBanner(root) {
    var banner = root.querySelector('.wfc-validation-error');
    if (!banner) {
      var container = root.querySelector('.wfc-container');
      if (!container) return;
      banner = document.createElement('div');
      banner.className = 'wfc-validation-error';
      banner.innerHTML = '<span>Please correct the errors above to see results</span>';
      var emptyEl = root.querySelector('.wfc-empty-state');
      if (emptyEl) container.insertBefore(banner, emptyEl);
      else container.appendChild(banner);
    }
    banner.style.display = '';
  }

  /* ────────────────────────────── Bootstrap ──────────────────────────────── */
  function initAll() {
    var roots = document.querySelectorAll('.wheel-fitment-calculator');
    for (var i = 0; i < roots.length; i++) initSection(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor: re-init on section reload
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector
      ? e.target.querySelector('.wheel-fitment-calculator')
      : null;
    if (root) initSection(root);
    else initAll();
  });
})();
